import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CloseRounded,
  DragIndicator,
  SearchRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button as MuiButton,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import { Resources } from '../resource';
import { PERPS_V1_PRESETS_PERMISSION } from '../perps-v1-shared/constants';
import { errorMessage } from '../perps-v1-shared/errorMessage';
import { FavoritePreset, PresetCandidate, candidatesApi, presetsApi } from './dataProvider';
import './init';

const MAX_PRESETS = 8;

interface PresetRow {
  symbol: string;
  label: string;
  dex: string;
  // Sourced straight from FavoritePreset.isDelisted (backend spec PRESETDEAD), not inferred —
  // authoritative for both a symbol flipped delisted and one vanished from coin metadata entirely.
  isDelisted: boolean;
}

function SortablePresetRow({
  row,
  index,
  count,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  row: PresetRow;
  index: number;
  count: number;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (symbol: string) => void;
}) {
  const translate = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.symbol,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1.25,
          border: '1px solid',
          borderRadius: 1.5,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ width: 24, textAlign: 'center', color: 'primary.main' }}
        >
          {index + 1}
        </Typography>
        <IconButton
          size="small"
          sx={{ cursor: 'grab', color: 'text.disabled' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {row.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.dex ? `${row.symbol} · ${row.dex}` : row.symbol}
          </Typography>
        </Box>
        {row.isDelisted ? (
          <Chip
            size="small"
            color="warning"
            label={translate('module.perpsV1Presets.delisted')}
          />
        ) : null}
        <PermissionButton resource={PERPS_V1_PRESETS_PERMISSION} action="update">
          <Stack direction="row" spacing={0}>
            <IconButton
              size="small"
              disabled={index === 0}
              onClick={() => onMoveUp(index)}
              aria-label={translate('module.perpsV1Presets.moveUp')}
            >
              <ArrowUpwardRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={index === count - 1}
              onClick={() => onMoveDown(index)}
              aria-label={translate('module.perpsV1Presets.moveDown')}
            >
              <ArrowDownwardRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onRemove(row.symbol)}
              aria-label={translate('module.perpsV1Presets.remove')}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Stack>
        </PermissionButton>
      </Stack>
    </div>
  );
}

function CandidateRow({
  candidate,
  selectedIndex,
  atMax,
  onAdd,
}: {
  candidate: PresetCandidate;
  selectedIndex?: number;
  atMax: boolean;
  onAdd: (symbol: string) => void;
}) {
  const translate = useTranslate();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ flex: 1, minWidth: 0, opacity: selectedIndex ? 0.55 : 1 }}>
        <Typography variant="subtitle2" noWrap>
          {candidate.displaySymbol}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {candidate.dex ? `${candidate.symbol} · ${candidate.dex}` : candidate.symbol}
        </Typography>
      </Box>
      {selectedIndex ? (
        <Chip
          size="small"
          variant="outlined"
          label={translate('module.perpsV1Presets.selectedBadge', { index: selectedIndex })}
          sx={{ color: 'text.disabled', borderColor: 'divider' }}
        />
      ) : (
        <PermissionButton resource={PERPS_V1_PRESETS_PERMISSION} action="update">
          <Tooltip title={atMax ? translate('module.perpsV1Presets.maxReached') : ''}>
            <span>
              <MuiButton
                size="small"
                variant="outlined"
                color="success"
                disabled={atMax}
                onClick={() => onAdd(candidate.symbol)}
              >
                {translate('module.perpsV1Presets.add')}
              </MuiButton>
            </span>
          </Tooltip>
        </PermissionButton>
      )}
    </Stack>
  );
}

const View = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [candidates, setCandidates] = useState<PresetCandidate[]>([]);
  const [serverPresets, setServerPresets] = useState<string[]>([]);
  const [draftPresets, setDraftPresets] = useState<string[]>([]);
  // symbol -> isDelisted, snapshotted from the last GET/PUT response. A symbol added from the
  // candidate pool during this session has no entry and defaults to live (false) below — the
  // candidate pool is the live universe by construction.
  const [delistedMap, setDelistedMap] = useState<Map<string, boolean>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');

  const applyPresets = useCallback((presets: FavoritePreset[]) => {
    const sorted = [...presets].sort((a, b) => a.displayOrder - b.displayOrder);
    setServerPresets(sorted.map((p) => p.symbol));
    setDraftPresets(sorted.map((p) => p.symbol));
    setDelistedMap(new Map(sorted.map((p) => [p.symbol, p.isDelisted])));
  }, []);

  // Load the candidate pool + the current favorite presets once on mount.
  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const [presetsResponse, candidatesResponse] = await Promise.all([
          dataProvider.getList(Resources.perpsV1Presets, {
            filter: {},
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'displayOrder', order: 'ASC' },
          }),
          candidatesApi.list(),
        ]);
        if (!active) return;
        applyPresets(presetsResponse?.data ?? []);
        setCandidates(candidatesResponse?.data ?? []);
      } catch (error: any) {
        if (!active) return;
        notify(
          errorMessage(error, translate('module.perpsV1Presets.loadError')),
          { type: 'error' }
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const candidateMap = useMemo(() => new Map(candidates.map((c) => [c.symbol, c])), [candidates]);

  // Draft-order badge for the candidates panel; must reflect the in-progress draft, not the saved order.
  const draftIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    draftPresets.forEach((symbol, index) => map.set(symbol, index + 1));
    return map;
  }, [draftPresets]);

  const isChanged = useMemo(
    () => JSON.stringify(draftPresets) !== JSON.stringify(serverPresets),
    [draftPresets, serverPresets]
  );

  const atMax = draftPresets.length >= MAX_PRESETS;

  const filteredCandidates = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return candidates;
    return candidates.filter(
      (c) => c.symbol.toLowerCase().includes(key) || c.displaySymbol.toLowerCase().includes(key)
    );
  }, [candidates, keyword]);

  const presetRows: PresetRow[] = useMemo(
    () =>
      draftPresets.map((symbol) => {
        const candidate = candidateMap.get(symbol);
        return {
          symbol,
          label: candidate?.displaySymbol ?? symbol,
          dex: candidate?.dex ?? '',
          isDelisted: delistedMap.get(symbol) ?? false,
        };
      }),
    [draftPresets, candidateMap, delistedMap]
  );

  const delistedCount = useMemo(
    () => presetRows.filter((r) => r.isDelisted).length,
    [presetRows]
  );

  const handleAdd = (symbol: string) => {
    setDraftPresets((prev) => {
      if (prev.includes(symbol) || prev.length >= MAX_PRESETS) return prev;
      return [...prev, symbol];
    });
  };

  const handleRemove = (symbol: string) => {
    setDraftPresets((prev) => prev.filter((s) => s !== symbol));
  };

  // One click for the "several dead at once" case (a delist sweep can take out more than one
  // preset in the same run); the per-row X below still covers the single-row case.
  const handleRemoveDelisted = () => {
    setDraftPresets((prev) => prev.filter((symbol) => !(delistedMap.get(symbol) ?? false)));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    setDraftPresets((prev) => {
      const next = index + direction;
      if (next < 0 || next >= prev.length) return prev;
      return arrayMove(prev, index, next);
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setDraftPresets((prev) => {
        const oldIndex = prev.indexOf(`${active.id}`);
        const newIndex = prev.indexOf(`${over.id}`);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleDiscard = () => {
    setDraftPresets(serverPresets);
  };

  const handleSave = async () => {
    // The Save button is already disabled while delistedCount > 0 (see below); this check is a
    // defensive backstop, not the primary gate — it exists so a stale render or a symbol that
    // delists in the moment between button-enable and click still gets our own explanation
    // instead of a doomed request round-tripping to the upstream "Invalid parameter — unknown:
    // <symbol>" message, which never says the word "delisted" and would leave the admin guessing.
    if (delistedCount > 0) {
      notify(translate('module.perpsV1Presets.delistedWarning', { count: delistedCount }), {
        type: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      const response = await presetsApi.save(draftPresets);
      applyPresets(response?.data ?? []);
      notify(translate('module.perpsV1Presets.saveSuccess'), { type: 'success' });
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Presets.saveError')),
        { type: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionGuard resource={PERPS_V1_PRESETS_PERMISSION} action="read">
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.perpsV1Presets.title')}
          links={[
            { name: translate('resource.perpsV1') },
            { name: translate('resource.perpsV1Presets') },
          ]}
          action={
            <PermissionButton resource={PERPS_V1_PRESETS_PERMISSION} action="update">
              <Stack direction="row" spacing={2}>
                <MuiButton
                  variant="outlined"
                  color="inherit"
                  onClick={handleDiscard}
                  disabled={loading || saving || !isChanged}
                >
                  {translate('module.perpsV1Presets.discard')}
                </MuiButton>
                <Tooltip
                  title={
                    !loading && delistedCount > 0
                      ? translate('module.perpsV1Presets.delistedWarning', { count: delistedCount })
                      : ''
                  }
                >
                  <span>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      loading={saving}
                      disabled={loading || !isChanged || delistedCount > 0}
                    >
                      {translate('module.perpsV1Presets.save')}
                    </LoadingButton>
                  </span>
                </Tooltip>
              </Stack>
            </PermissionButton>
          }
          sx={{ mb: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('module.perpsV1Presets.subtitle')}
        </Typography>

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
          <Card sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Typography variant="h6">{translate('module.perpsV1Presets.candidates')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {translate('module.perpsV1Presets.totalCount', { count: filteredCandidates.length })}
              </Typography>
            </Stack>
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1.5, mb: 1.5 }}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder={translate('module.perpsV1Presets.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flex: 1, minHeight: 420, position: 'relative' }}>
              <Stack spacing={0} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 0.5 }}>
                {loading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {translate('module.perpsV1Presets.loading')}
                  </Typography>
                ) : filteredCandidates.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {translate('module.perpsV1Presets.noResult')}
                  </Typography>
                ) : (
                  filteredCandidates.map((candidate) => (
                    <CandidateRow
                      key={candidate.symbol}
                      candidate={candidate}
                      selectedIndex={draftIndexMap.get(candidate.symbol)}
                      atMax={atMax}
                      onAdd={handleAdd}
                    />
                  ))
                )}
              </Stack>
            </Box>
          </Card>

          <Card sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              <Typography variant="h6">{translate('module.perpsV1Presets.selected')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {translate('module.perpsV1Presets.totalCount', { count: draftPresets.length })}
              </Typography>
            </Stack>
            {/* 保存时后端会对整份列表做校验，只要其中还有已下架的 symbol 就整份保存失败。
                这里提前说明，否则管理员只会看到一句带 symbol 名的错误提示，不知道该做什么。 */}
            {delistedCount > 0 ? (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ mt: 1.5 }}
                action={
                  <PermissionButton resource={PERPS_V1_PRESETS_PERMISSION} action="update">
                    <MuiButton color="warning" size="small" onClick={handleRemoveDelisted}>
                      {translate('module.perpsV1Presets.removeDelisted')}
                    </MuiButton>
                  </PermissionButton>
                }
              >
                {translate('module.perpsV1Presets.delistedWarning', { count: delistedCount })}
              </Alert>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 420, position: 'relative', mt: 2 }}>
              <Stack spacing={1.5} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 0.5 }}>
                {loading ? (
                  <Typography variant="body2" color="text.secondary">
                    {translate('module.perpsV1Presets.loading')}
                  </Typography>
                ) : presetRows.length === 0 ? (
                  <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('module.perpsV1Presets.empty')}
                    </Typography>
                  </Box>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={draftPresets} strategy={verticalListSortingStrategy}>
                      <Stack spacing={1.5}>
                        {presetRows.map((row, index) => (
                          <SortablePresetRow
                            key={row.symbol}
                            row={row}
                            index={index}
                            count={presetRows.length}
                            onMoveUp={(i) => handleMove(i, -1)}
                            onMoveDown={(i) => handleMove(i, 1)}
                            onRemove={handleRemove}
                          />
                        ))}
                      </Stack>
                    </SortableContext>
                  </DndContext>
                )}
              </Stack>
            </Box>
          </Card>
        </Box>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
